/**
 * Where the crisis resource cards go in a conversation (ChatView).
 *
 * A card belongs to its turn but is drawn under that turn's answer: the words
 * lead and the numbers sit right beneath them. While the answer is still being
 * written the card waits under it, on screen from the start. A turn that ends
 * with no answer (stopped, failed) leaves its card where the conversation
 * stopped, so the card never moves under the answer to a different question,
 * such as a "Go deeper" on an earlier one.
 *
 * Cards and answers carry their turn (the request id). A card from before
 * turns were recorded goes under the next answer, as it always did.
 *
 *   messages  the conversation, cards included ({ safetyCard, turn })
 *   isAnswer  (message) => true for an answer from the model
 *   liveTurn  the turn being written right now, or null
 *
 * Returns { shownMessages, cardsUnderStream }.
 */
export function placeCrisisCards(messages, { isAnswer, liveTurn = null }) {
  const sameTurn = (card, turn) => !card.turn || !turn || card.turn === turn;
  const shown = [];
  let held = [];
  for (const m of messages) {
    if (m.safetyCard) {
      held.push(m);
      continue;
    }
    if (m.role === 'user') {
      shown.push(...held);
      held = [];
    } else if (isAnswer(m)) {
      // A card from another turn is set down before this answer, not under it.
      shown.push(...held.filter((card) => !sameTurn(card, m.turn)));
      held = held.filter((card) => sameTurn(card, m.turn));
    }
    shown.push(m);
    if (isAnswer(m)) {
      shown.push(...held);
      held = [];
    }
  }
  const cardsUnderStream = liveTurn ? held.filter((card) => sameTurn(card, liveTurn)) : [];
  return {
    shownMessages: shown.concat(held.filter((card) => !cardsUnderStream.includes(card))),
    cardsUnderStream,
  };
}

/** The turns that showed a crisis card, so "Listen" on their answer knows. */
export function crisisTurns(messages) {
  return new Set(messages.filter((m) => m.safetyCard && m.turn).map((m) => m.turn));
}
